import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { Modal, ModalBody, ModalHeader, Button, Input } from 'reactstrap';
import {
    BiPlusCircle,
    BiEditAlt,
    BiChevronRight,
    BiMessageSquareDetail,
    BiUser,
    BiX,
    BiPhone,
    BiCheck,
    BiUserPlus,
    BiSearch,
    BiHash,
} from 'react-icons/bi';
import { FiTrash2, FiArchive } from 'react-icons/fi';
import { MdQrCode } from 'react-icons/md';
import { QRCodeCanvas } from 'qrcode.react';
import FilterSelector from '../../trivia/Components/ShareTrivia/FilterSelector';
import PaginatedUserList from '../../trivia/Components/ShareTrivia/PaginatedUserList';
import { LayoutRow, LayoutHeading } from '../../weekly-wins/TableLayoutComponents';
import DeleteHashtagModal from './DeleteHashtagModal';
import RecoverDeletedHashtagsModal from './RecoverDeletedHashtagsModal';
import axios from '../../../axios';
import * as url from '../../../utils/url';
import { toastContainer } from '../../../utils/toast';
import { baseUrl } from '../../../utils/constants';
import { getOrgPrefix, stripHashtagPrefix } from '../../../utils/hashtagPrefix';
import BottomScrollListener from 'react-bottom-scroll-listener';
import { orgSetupSkipped } from '../../../modules/actions/user';
import OoPopover from '../../../components/UI/Popover/OoPopover';

const DEFAULT_HASHTAG_NAMES = ['Safety', 'Quality', 'HR', 'Leader'];

// Twilio Textify number per environment (follows same pattern as STRIPE_PUBLIC_KEY in constants.js)
const TWILIO_TEXTIFY_NUMBER = window.location.hostname.indexOf('trivvy.ouroffice.io') > -1
    ? '+1 (833) 949-2909'   // Prod
    : '+1 (888) 247-5579';  // QA + local/dev

const NOTIFICATION_FREQUENCY = { FIRST_TIME: 'FIRST_TIME', EVERY_TIME: 'EVERY_TIME', NONE: 'NONE' };

const SmsRoutingModal = ({ isOpen, toggle, onSave, user, onUpdateOrgPrefix }) => {
    const [currentStep, setCurrentStep] = useState(0); // 0: Prefix, 1: List, 2: Picker
    const [prefix, setPrefix] = useState('');
    const [prefixInput, setPrefixInput] = useState('');
    const [isSavingPrefix, setIsSavingPrefix] = useState(false);
    const [hashtags, setHashtags] = useState([
        { id: '1', name: 'Safety', recipients: [], isDefault: true, isEditing: false },
        { id: '2', name: 'Quality', recipients: [], isDefault: true, isEditing: false },
        { id: '3', name: 'HR', recipients: [], isDefault: true, isEditing: false },
        { id: '4', name: 'Leader', recipients: [], isDefault: true, isEditing: false },
    ]);
    const [activeHashtagId, setActiveHashtagId] = useState(null);
    const [allMetaData, setAllMetaData] = useState({});
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [editingValue, setEditingValue] = useState('');
    const [newUserPhone] = useState(TWILIO_TEXTIFY_NUMBER);
    const [pickerTab, setPickerTab] = useState('current'); // 'current' | 'find'
    const [currentTabSearch, setCurrentTabSearch] = useState('');
    const [findTabKey, setFindTabKey] = useState(0);
    // TBV-916: demo-only display state, not persisted — SMS alerts don't function yet.
    const [smsAlertFrequency, setSmsAlertFrequency] = useState(NOTIFICATION_FREQUENCY.FIRST_TIME);
    // TBV-916 update: "None" for Email is demo-only too — the backend's real notificationFrequency
    // field only accepts FIRST_TIME/EVERY_TIME, so selecting None never overwrites its saved value.
    const [emailAlertIsNone, setEmailAlertIsNone] = useState(false);

    // Auto-register toggle
    const [autoRegister, setAutoRegister] = useState(false);
    const [isSavingAutoRegister, setIsSavingAutoRegister] = useState(false);

    // Delete / Recover Deleted
    const [hashtagPendingDelete, setHashtagPendingDelete] = useState(null);
    const [isRecoverModalOpen, setIsRecoverModalOpen] = useState(false);
    const [deletedHashtags, setDeletedHashtags] = useState([]);
    const [deletedNextSkip, setDeletedNextSkip] = useState(0);
    const [hasMoreDeleted, setHasMoreDeleted] = useState(false);
    const [isLoadingDeleted, setIsLoadingDeleted] = useState(false);

    // Pagination for Textify Groups
    const [nextSkip, setNextSkip] = useState(0);
    const [hasMoreGroups, setHasMoreGroups] = useState(true);
    const [isLoadingGroups, setIsLoadingGroups] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        const existingPrefix = user && user.profile && user.profile.organizationId && user.profile.organizationId.smsGroupPrefix;
        if (existingPrefix) {
            const clean = existingPrefix.endsWith('-') ? existingPrefix.slice(0, -1) : existingPrefix;
            setPrefix(clean.toLowerCase());
            setPrefixInput(clean.toLowerCase());
            setCurrentStep(1);
        } else {
            setPrefix('');
            setPrefixInput('');
            setCurrentStep(0);
        }
        var orgAutoRegister = user && user.profile && user.profile.organizationId && user.profile.organizationId.smsAutoRegister;
        setAutoRegister(orgAutoRegister === true);
        // Fetch fresh org data to get current smsAutoRegister value
        var modalOrgId = user && user.profile && user.profile.organizationId && user.profile.organizationId._id;
        if (modalOrgId) {
            axios.get(url.updateOrgById(modalOrgId))
                .then(function(res) {
                    var orgData = res && res.data && res.data.data;
                    if (orgData && typeof orgData.smsAutoRegister === 'boolean') {
                        setAutoRegister(orgData.smsAutoRegister);
                    }
                })
                .catch(function() {});
        }
        setActiveHashtagId(null);
        setPickerTab('current');
        setCurrentTabSearch('');
        setHashtagPendingDelete(null);
        setIsRecoverModalOpen(false);
        setDeletedHashtags([]);
        setDeletedNextSkip(0);
        setHasMoreDeleted(false);
        axios.get(url.getOrgUserMetaData)
            .then((response) => {
                if (response && response.data && response.data.data && response.data.data.data) {
                    setAllMetaData(response.data.data.data);
                }
            })
            .catch((err) => {
                console.log(err, 'error fetching metadata');
            });
        fetchNextPage(0);
        fetchDeletedHashtags(0);
    }, [isOpen]);

    const handleSavePrefix = () => {
        const clean = prefixInput.trim().toLowerCase();
        if (clean.length < 2) return;
        const orgId = user && user.profile && user.profile.organizationId && user.profile.organizationId._id;
        setIsSavingPrefix(true);
        axios.put(url.updateOrgById(orgId), { data: { smsGroupPrefix: clean } })
            .then(() => {
                setPrefix(clean);
                setCurrentStep(1);
                onUpdateOrgPrefix({ smsGroupPrefix: clean });
            })
            .catch((err) => {
                console.error('[SMS Modal] Error saving prefix:', err);
                var errMsg = (err.response && err.response.data && err.response.data.message) || 'Failed to save prefix. Please try again.';
                toastContainer(errMsg, 'error');
            })
            .finally(() => setIsSavingPrefix(false));
    };

    const handleToggleAutoRegister = () => {
        var orgId = user && user.profile && user.profile.organizationId && user.profile.organizationId._id;
        if (!orgId) return;
        var newValue = !autoRegister;
        setIsSavingAutoRegister(true);
        setAutoRegister(newValue);
        axios.put(url.updateOrgById(orgId), { data: { smsAutoRegister: newValue } })
            .catch(function(err) {
                console.error('[SMS Modal] Error saving auto-register:', err);
                setAutoRegister(!newValue);
                toastContainer('Failed to update setting.', 'error');
            })
            .finally(function() { setIsSavingAutoRegister(false); });
    };

    const fetchNextPage = (skipValue) => {
        if (!isOpen || isLoadingGroups) return;
        setIsLoadingGroups(true);

        axios.get(`${url.textifyListGroups}?skip=${skipValue}`)
            .then((res) => {
                const data = res.data || {};
                const fetchedGroups = data.items || [];

                setHashtags((prev) => {
                    const defaultNames = ['Safety', 'Quality', 'HR', 'Leader'];

                    // Always seed with the 4 defaults on the first page load
                    let updated = skipValue === 0 ? defaultNames.map((name, i) => ({
                        id: String(i + 1),
                        name: name,
                        standardName: name, // Preserve the original default name even if renamed
                        recipients: [],
                        existingRecipientCount: 0,
                        isDefault: true,
                        isEditing: false
                    })) : [...prev];

                    const orgPrefix = getOrgPrefix(user);

                    fetchedGroups.forEach((group) => {
                        // Strips the org prefix if the BE already prepended it (e.g. "acme-wednesday" → "wednesday")
                        const rawName = stripHashtagPrefix(group.groupName, orgPrefix);

                        // If the backend has a standardName, strip the hashtag to match our local seeds
                        const backendStandardName = group.standardName ? (group.standardName.startsWith('#') ? group.standardName.slice(1) : group.standardName) : rawName;

                        const defaultIdx = updated.findIndex((h) => h.isDefault && (h.standardName || h.name).toLowerCase() === backendStandardName.toLowerCase());

                        if (defaultIdx !== -1) {
                            // Merge backend configuration into the seeded default
                            updated[defaultIdx] = {
                                ...updated[defaultIdx],
                                id: group._id, // use real database ID as UI key
                                _id: group._id,
                                name: rawName, // Keep whatever the user manually renamed it to!
                                existingRecipientCount: group.recipientCount,
                            };
                        } else {
                            // It's a custom hashtag or an unrecognized default
                            updated.push({
                                id: group._id,
                                _id: group._id,
                                name: rawName,
                                recipients: [],
                                existingRecipientCount: group.recipientCount,
                                isDefault: group.isStandard || defaultNames.includes(rawName),
                                isEditing: false
                            });
                        }
                    });

                    return updated;
                });

                setNextSkip(data.nextSkip || 0);
                setHasMoreGroups(!!data.hasMore);
            })
            .catch((err) => console.log(err, 'error fetching textify groups'))
            .finally(() => setIsLoadingGroups(false));
    };

    // Flat list for Recover Deleted — deliberately skips fetchNextPage's
    // defaults-seed/merge logic, which doesn't apply to a deleted list.
    const fetchDeletedHashtags = (skipValue) => {
        if (!isOpen || isLoadingDeleted) return;
        setIsLoadingDeleted(true);

        axios.get(`${url.textifyListGroups}?skip=${skipValue}&status=inactive`)
            .then((res) => {
                const data = res.data || {};
                const fetchedGroups = data.items || [];
                const orgPrefix = getOrgPrefix(user);

                const mapped = fetchedGroups.map((group) => {
                    const rawName = stripHashtagPrefix(group.groupName, orgPrefix);
                    const isDefault = DEFAULT_HASHTAG_NAMES.some((n) => n.toLowerCase() === rawName.toLowerCase());
                    return {
                        id: group._id,
                        _id: group._id,
                        name: rawName,
                        isDefault: isDefault,
                        recipientCount: group.recipientCount || 0,
                    };
                });

                setDeletedHashtags((prev) => (skipValue === 0 ? mapped : [...prev, ...mapped]));
                setDeletedNextSkip(data.nextSkip || 0);
                setHasMoreDeleted(!!data.hasMore);
            })
            .catch((err) => console.log(err, 'error fetching deleted textify groups'))
            .finally(() => setIsLoadingDeleted(false));
    };

    const handleBottomScroll = () => {
        if (hasMoreGroups && !isLoadingGroups && currentStep === 1) {
            fetchNextPage(nextSkip);
        }
    };

    // Use a ref so handleApplyFilters always reads the latest activeHashtagId
    const activeHashtagIdRef = React.useRef(activeHashtagId);
    useEffect(() => {
        activeHashtagIdRef.current = activeHashtagId;
    }, [activeHashtagId]);

    const handleApplyFilters = useCallback(async (query) => {
        const currentHashtagId = activeHashtagIdRef.current;

        // Decode the double-encoded query string from FilterSelector
        let decodedQuery = '';
        try {
            decodedQuery = decodeURIComponent(decodeURIComponent(query));
        } catch (e) {
            try { decodedQuery = decodeURIComponent(query); }
            catch (e2) { decodedQuery = query; }
        }

        const filterMap = {};
        decodedQuery.split('&').forEach((part) => {
            const match = part.match(/filters\[([^\]]+)\]=(.+)/i);
            if (match) {
                const key = match[1];
                const value = match[2];
                if (!filterMap[key]) filterMap[key] = [];
                filterMap[key].push(value);
            }
        });
        const filtersJson = Object.keys(filterMap).map((key) => ({ key, values: filterMap[key] }));

        setUsersLoading(true);
        try {
            const params = new URLSearchParams({
                includeMe: 'true',
                type: 'EMAIL',
                page: 1,
                limit: 50,
                filters: JSON.stringify(filtersJson),
                deletedIds: '',
                dateArray: '',
                numberArray: '',
            });
            const response = await axios.get(`${url.userListArray}?${params.toString()}`);

            const rawUsers = (response.data && response.data.data && (response.data.data.emailUsers || [])) || [];
            // Recipients are platform users (admins) — mobileNumber is not required for them
            const users = rawUsers;

            const mapped = users.map((u) => ({
                ...u,
                userId: u._id || u.userId,
                value: u._id || u.userId,
                label: (u.firstName || '') + ' ' + (u.lastName || ''),
            }));

            setFilteredUsers(mapped);
        } catch (err) {
            console.error('[SMS Modal] API error:', err);
            toastContainer('Failed to fetch users. Please try again.', 'error');
        } finally {
            setUsersLoading(false);
        }
    }, []);

    const addCustomHashtag = () => {
        setEditingValue('');
        const newHashtag = {
            id: Date.now().toString(),
            name: '',
            recipients: [],
            isDefault: false,
            isEditing: true
        };
        setHashtags((prev) => [...prev, newHashtag]);
    };

    // Always include the signed-in user (group creator) in every recipients payload
    var creatorId = user && user.profile && user.profile._id;
    var creatorMobile = (user && user.profile && user.profile.mobileNumber) || '';
    var withCreator = function(list) {
        if (!creatorId) return list;
        var alreadyIn = list.some(function(r) { return r.userId === creatorId; });
        if (alreadyIn) return list;
        return list.concat([{ userId: creatorId, mobileNumber: creatorMobile }]);
    };

    const updateHashtagName = (id, rawNewName) => {
        // Strips '#' before the prefix: with it attached the prefix check misses
        // and the prefix gets saved into the name (bug865).
        const newName = stripHashtagPrefix(rawNewName.trim().toLowerCase(), prefix);
        if (!newName) {
            setHashtags((prev) => prev.filter((h) => h.id !== id || h.isDefault));
            return;
        }
        if (hashtags.some((h) => h.id !== id && h.name.toLowerCase() === newName.toLowerCase())) {
            toastContainer('Hashtag name must be unique.', 'error');
            return;
        }

        const target = hashtags.find(h => h.id === id);

        setHashtags((prev) => prev.map((h) => h.id === id ? { ...h, name: newName, isEditing: false } : h));

        if (target) {
            if (target._id) {
                const payload = { groupId: target._id, groupName: newName, isStandard: target.isDefault };
                if (target.isDefault) {
                    payload.standardName = `#${target.standardName || target.name}`;
                }
                axios.patch(url.textifyUpdateGroup, payload).catch(err => {
                    const errMsg = (err.response && err.response.data && err.response.data.message) || (err.response && err.response.data && err.response.data.error) || 'Failed to rename remote group.';
                    // The rename was applied optimistically above and rejected, so put
                    // the row back into edit mode holding the attempted name — the
                    // user only needs to adjust it, not retype it.
                    setEditingValue(newName);
                    setHashtags((prev) => prev.map((h) => h.id === id ? { ...h, name: target.name, isEditing: true } : h));
                    toastContainer(errMsg, 'error');
                });
            } else {
                const payload = {
                    hashtagName: newName,
                    isStandard: target.isDefault,
                    recipients: withCreator([])
                };
                if (target.description && target.description.trim() !== '') {
                    payload.description = target.description.trim();
                }
                if (target.isDefault) {
                    payload.standardName = `#${target.standardName || target.name}`;
                }

                axios.post(url.textifyCreateGroup, payload).then(res => {
                    const newId = res.data && res.data._id;
                    if (newId) {
                        setHashtags((prev) => prev.map((h) => h.id === id ? { ...h, _id: newId } : h));
                    }
                    // toastContainer dismisses the previous toast first, so this also
                    // clears the collision error when the retry succeeds.
                    toastContainer('Hashtag set up successfully.', 'success');
                }).catch(err => {
                    const errMsg = (err.response && err.response.data && err.response.data.message) || (err.response && err.response.data && err.response.data.error) || 'Failed to create group.';
                    // Nothing was persisted. Rather than dropping the row (which makes
                    // the user retype from scratch), put it back into edit mode with
                    // what they typed still there so they can just adjust the name.
                    setEditingValue(newName);
                    setHashtags((prev) => prev.map((h) => h.id === id ? { ...h, isEditing: true } : h));
                    toastContainer(errMsg, 'error');
                });
            }
        }
    };

    const deleteHashtag = (id) => {
        const target = hashtags.find(h => h.id === id);
        if (target && target._id) {
            axios.patch(url.textifyUpdateGroupStatus(target._id), { status: "inactive" }).catch(err => {
                const errMsg = (err.response && err.response.data && err.response.data.message) || (err.response && err.response.data) || 'Failed to delete remote group.';
                toastContainer(errMsg, 'error');
            });
        }
        setHashtags((prev) => prev.filter((h) => h.id !== id));
        if (activeHashtagId === id) setActiveHashtagId(null);
    };

    // Standard Hashtags are fixed slots, so a default row reverts to its
    // unconfigured shape instead of being removed (see isDeletedDefault below).
    const handleHashtagDeleted = (deletedHashtag) => {
        if (deletedHashtag.isDefault) {
            setHashtags((prev) => prev.map((h) => h.id === deletedHashtag.id
                ? { ...h, _id: undefined, recipients: [], existingRecipientCount: 0, isEditing: false }
                : h));
        } else {
            setHashtags((prev) => prev.filter((h) => h.id !== deletedHashtag.id));
        }
        if (activeHashtagId === deletedHashtag.id) setActiveHashtagId(null);

        // Only track it as recoverable if it was actually persisted server-side.
        if (deletedHashtag._id) {
            setDeletedHashtags((prev) => {
                if (prev.some((d) => d.id === deletedHashtag._id)) return prev;
                const recipientCount = Math.max(
                    (deletedHashtag.recipients && deletedHashtag.recipients.length) || 0,
                    deletedHashtag.existingRecipientCount || 0
                );
                return [{
                    id: deletedHashtag._id,
                    _id: deletedHashtag._id,
                    name: deletedHashtag.name,
                    standardName: deletedHashtag.standardName,
                    isDefault: deletedHashtag.isDefault,
                    recipientCount: recipientCount,
                }, ...prev];
            });
        }
        setHashtagPendingDelete(null);
    };

    // Re-runs fetchNextPage so a restored default re-merges under Standard
    // Hashtags (not Custom) via the same merge logic as every other load.
    const handleHashtagRestored = (restoredId) => {
        setDeletedHashtags((prev) => prev.filter((d) => d.id !== restoredId));
        fetchNextPage(0);
    };

    const toggleRecipient = useCallback((userObj) => {
        setHashtags((prev) => prev.map((h) => {
            if (h.id !== activeHashtagId) return h;
            const existingIdx = h.recipients.findIndex(r => r.userId === userObj.userId);

            if (existingIdx !== -1) {
                const updated = [...h.recipients];
                const target = updated[existingIdx];

                if (target.isNew) {
                    updated.splice(existingIdx, 1);
                } else {
                    updated[existingIdx] = { ...target, isMarkedForRemoval: !target.isMarkedForRemoval };
                }
                return { ...h, recipients: updated };
            } else {
                return { ...h, recipients: [...h.recipients, { ...userObj, isNew: true, dateCreated: new Date().toISOString() }] };
            }
        }));
    }, [activeHashtagId]);

    const removeRecipient = (userId) => {
        setHashtags((prev) => prev.map((h) => {
            if (h.id !== activeHashtagId) return h;
            return {
                ...h,
                recipients: h.recipients.map(r => r.userId === userId ? { ...r, isMarkedForRemoval: true } : r)
            };
        }));
    };

    const undoRemoveRecipient = (userId) => {
        setHashtags((prev) => prev.map((h) => {
            if (h.id !== activeHashtagId) return h;
            return {
                ...h,
                recipients: h.recipients.map(r => r.userId === userId ? { ...r, isMarkedForRemoval: false } : r)
            };
        }));
    };

    const activeHashtag = useMemo(
        () => hashtags.find((h) => h.id === activeHashtagId),
        [hashtags, activeHashtagId]
    );

    // Default names currently in the deleted list — lets a reverted default
    // row read as "Deleted" instead of looking identical to "never configured".
    const deletedDefaultNames = useMemo(
        () => new Set(deletedHashtags.filter((d) => d.isDefault).map((d) => (d.standardName || d.name).toLowerCase())),
        [deletedHashtags]
    );

    const getInitials = (u) => {
        const first = (u.firstName && u.firstName.charAt(0)) || '';
        const last = (u.lastName && u.lastName.charAt(0)) || '';
        return (first + last).toUpperCase();
    };

    const goToPicker = async (hashtagId) => {
        const targetGroup = hashtags.find(h => h.id === hashtagId);

        if (targetGroup && targetGroup._id) {
            setUsersLoading(true);
            try {
                const res = await axios.get(url.textifyGetGroupDetails(targetGroup._id));
                const fetchedRaw = (res && res.data && res.data.recipients) || [];
                const fetchedRecipients = fetchedRaw.map(r => {
                    const parts = (r.name || '').trim().split(' ');
                    const first = parts.length > 0 ? parts[0] : '';
                    const last = parts.length > 1 ? parts.slice(1).join(' ') : '';
                    return {
                        ...r,
                        firstName: first,
                        lastName: last,
                        userId: r.userId || r._id
                    };
                });

                const fetchedFrequency = (res && res.data && res.data.notificationFrequency) || NOTIFICATION_FREQUENCY.FIRST_TIME;
                setHashtags((prev) => prev.map(h =>
                    h.id === hashtagId
                        ? { ...h, recipients: fetchedRecipients, initialRecipients: fetchedRecipients.map(r => r.userId), description: (res && res.data && res.data.description) || '', initialDescription: (res && res.data && res.data.description) || '', notificationFrequency: fetchedFrequency, initialNotificationFrequency: fetchedFrequency }
                        : h
                ));
            } catch (err) {
                console.error('[SMS Modal] Error fetching detailed group configuration:', err);
                toastContainer('Failed to load group details from server.', 'error');
            } finally {
                setUsersLoading(false);
            }
        }

        setActiveHashtagId(hashtagId);
        // TBV-916: reset both demo-only selections so they don't carry over to a different hashtag.
        setSmsAlertFrequency(NOTIFICATION_FREQUENCY.FIRST_TIME);
        setEmailAlertIsNone(false);
        setPickerTab('current');
        setCurrentTabSearch('');
        setCurrentStep(2);
    };

    const goToList = () => {
        setCurrentStep(1);
        setFilteredUsers([]);
        setFindTabKey((k) => k + 1);
    };

    const handleSaveRecipients = async () => {
        if (!activeHashtagId) return;
        let target = hashtags.find(h => h.id === activeHashtagId);
        if (!target) return;
        let stayInPicker = false;

        // Dummy emails (e.g. "temp_xxx@exampleemail.com" from auto-register) count as no email.
        const isInvalidEmail = (r) => {
            const email = (r.email || '').toString().trim();
            return !email || email.endsWith('@exampleemail.com');
        };
        const hasInvalidEmail = (target.recipients || [])
            .filter(r => r.isNew && !r.isMarkedForRemoval)
            .some(isInvalidEmail);
        if (hasInvalidEmail) {
            toastContainer(
                'Recipients must have an Email. Please go to the Users List to enter an email for the recipient you are trying to add. And then come back here to add them to this Hashtag.',
                'error'
            );
            // Drop no-email staged recipients from the UI so the green "Added" badge disappears.
            const filteredRecipients = (target.recipients || []).filter(
                r => !(r.isNew && !r.isMarkedForRemoval && isInvalidEmail(r))
            );
            target = { ...target, recipients: filteredRecipients };
            setHashtags((prev) => prev.map((h) => {
                if (h.id !== activeHashtagId) return h;
                return { ...h, recipients: filteredRecipients };
            }));
            // If valid new recipients remain, continue saving them. Otherwise stop.
            const hasValidNew = filteredRecipients.some(r => r.isNew && !r.isMarkedForRemoval);
            const hasRemovals = filteredRecipients.some(r => r.isMarkedForRemoval);
            if (!hasValidNew && !hasRemovals) {
                return;
            }
        }

        try {
            if (target._id) {
                const requests = [];

                // Only send recipients PATCH if recipients actually changed
                const currentRecipientIds = target.recipients
                    .filter(r => !r.isMarkedForRemoval)
                    .map(r => r.userId)
                    .slice()
                    .sort();
                const initialRecipientIds = (target.initialRecipients || []).slice().sort();
                const recipientsChanged = JSON.stringify(currentRecipientIds) !== JSON.stringify(initialRecipientIds);

                if (recipientsChanged) {
                    const recipientsPayload = {
                        groupId: target._id,
                        isStandard: target.isDefault,
                        recipients: target.recipients.filter(r => !r.isMarkedForRemoval).map(r => ({
                            userId: r.userId,
                            mobileNumber: r.mobileNumber || ''
                        }))
                    };
                    if (target.isDefault) recipientsPayload.standardName = `#${target.standardName || target.name}`;
                    requests.push(axios.patch(url.textifyUpdateGroup, recipientsPayload));
                }

                // Only send description PATCH if description actually changed
                if ((target.description || '') !== (target.initialDescription || '')) {
                    const descPayload = {
                        groupId: target._id,
                        isStandard: target.isDefault,
                        description: target.description || ''
                    };
                    if (target.isDefault) descPayload.standardName = `#${target.standardName || target.name}`;
                    requests.push(axios.patch(url.textifyUpdateGroup, descPayload));
                }

                // Only send notificationFrequency PATCH if it actually changed
                const currentFrequency = target.notificationFrequency || NOTIFICATION_FREQUENCY.FIRST_TIME;
                const initialFrequency = target.initialNotificationFrequency || NOTIFICATION_FREQUENCY.FIRST_TIME;
                if (currentFrequency !== initialFrequency) {
                    const freqPayload = {
                        groupId: target._id,
                        isStandard: target.isDefault,
                        notificationFrequency: currentFrequency
                    };
                    if (target.isDefault) freqPayload.standardName = `#${target.standardName || target.name}`;
                    requests.push(axios.patch(url.textifyUpdateGroup, freqPayload));
                }

                const recipientsAdded = target.recipients.filter(r => r.isNew && !r.isMarkedForRemoval).length > 0;
                const recipientsRemoved = target.recipients.some(r => r.isMarkedForRemoval);
                if (requests.length > 0) await Promise.all(requests);
                stayInPicker = true;
                setHashtags(prev => prev.map(h => {
                    if (h.id !== activeHashtagId) return h;
                    const confirmedRecipients = h.recipients
                        .filter(r => !r.isMarkedForRemoval)
                        .map(r => ({ ...r, isNew: false }));
                    return {
                        ...h,
                        initialDescription: target.description || '',
                        initialNotificationFrequency: target.notificationFrequency || NOTIFICATION_FREQUENCY.FIRST_TIME,
                        recipients: confirmedRecipients,
                        initialRecipients: confirmedRecipients.map(r => r.userId),
                    };
                }));
                setCurrentTabSearch('');
                setPickerTab('current');
                setFilteredUsers([]);
                setFindTabKey((k) => k + 1);
                const toastMsg = recipientsAdded ? 'Recipient(s) added successfully.' : recipientsRemoved ? 'Recipient(s) removed successfully.' : 'Hashtag set up successfully.';
                toastContainer(toastMsg, 'success');
            } else {
                const payload = {
                    hashtagName: target.name,
                    isStandard: target.isDefault,
                    notificationFrequency: target.notificationFrequency || NOTIFICATION_FREQUENCY.FIRST_TIME,
                    recipients: withCreator(target.recipients.filter(r => !r.isMarkedForRemoval).map(r => ({
                        userId: r.userId,
                        mobileNumber: r.mobileNumber || ''
                    })))
                };
                if (target.description && target.description.trim() !== '') {
                    payload.description = target.description.trim();
                }
                if (target.isDefault) payload.standardName = `#${target.standardName || target.name}`;

                const res = await axios.post(url.textifyCreateGroup, payload);
                const newId = res.data && res.data._id;
                if (newId) {
                    setHashtags(prev => prev.map(h => h.id === activeHashtagId ? { ...h, _id: newId, initialDescription: target.description || '', initialNotificationFrequency: target.notificationFrequency || NOTIFICATION_FREQUENCY.FIRST_TIME } : h));
                }
                toastContainer('Hashtag set up successfully.', 'success');
                fetchNextPage(0);
            }
        } catch (err) {
            console.error('Error saving group configuration:', err);
            const errMsg = (err.response && err.response.data && err.response.data.message) || (err.response && err.response.data) || 'Failed to save group.';
            toastContainer(errMsg, 'error');
        }
        if (!stayInPicker) {
            goToList();
        }
    };

    const renderPrefixStep = () => (
        <div style={{ padding: '16px 0' }}>
            <div className="text-center" style={{ marginBottom: '28px' }}>
                <div style={{ width: '64px', height: '64px', background: '#eff6ff', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                    <BiHash size={30} style={{ color: '#3b82f6' }} />
                </div>
                <h5 style={{ fontWeight: '700', fontSize: '18px', marginBottom: '8px' }}>Set Your Organization's Unique Prefix</h5>
                <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>Type a unique prefix to identify your organization's SMS chat.</p>
            </div>
            <div style={{ marginBottom: '24px' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b', marginBottom: '8px', display: 'block' }}>PREFIX</label>
                <Input
                    type="text"
                    placeholder="e.g. acme, plantops, teamalpha"
                    value={prefixInput}
                    onChange={(e) => setPrefixInput(e.target.value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase())}
                    style={{ borderRadius: '8px', padding: '12px 16px', fontSize: '15px', border: '2px solid #3b82f6' }}
                    autoFocus
                />
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '6px' }}>Minimum 2 characters. This prefix is inserted in all your hashtags for SMS chats.</div>
            </div>
            <Button
                color="primary"
                block
                disabled={prefixInput.trim().length < 2 || isSavingPrefix}
                onClick={handleSavePrefix}
                className={'btn-prefix-continue' + (prefixInput.trim().length >= 2 ? ' is-ready' : '')}
                style={{ borderRadius: '12px', padding: '14px', fontWeight: '700', border: 'none' }}
            >
                Continue to Hashtag Setup <BiChevronRight size={18} />
            </Button>
        </div>
    );

    const renderListStep = () => (
        <div className="sms-routing-liststep">
        <BottomScrollListener offset={50} onBottom={handleBottomScroll}>
            {(scrollRef) => (
                <div ref={scrollRef} className="sms-routing-modal-scroll">
                    {prefix && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: '#fff', border: '1px solid #e8edf2', borderRadius: '10px', marginBottom: '6px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <BiCheck size={18} style={{ color: '#22c55e' }} />
                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>Prefix: <span style={{ color: '#3b82f6' }}>{prefix}</span></span>
                            </div>
                            <button onClick={() => setCurrentStep(0)} style={{ fontSize: '13px', color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '500' }}>← Change</button>
                        </div>
                    )}
                    <div className="sms-section-title">Standard Hashtags</div>
                    {hashtags.filter((h) => h.isDefault).map((h) => (
                        <div key={h.id} className={'hashtag-card' + (!h._id ? ' not-configured' : '')} onClick={() => { if (!h.isEditing) goToPicker(h.id); }}>
                            <div className="d-flex align-items-center flex-grow-1">
                                <div className={`status-dot ${h.recipients.length > 0 || h.existingRecipientCount > 0 ? 'status-active' : 'status-inactive'}`} />
                                {h.isEditing ? (
                                    <div className="d-flex align-items-center" onClick={(e) => e.stopPropagation()}>
                                        <span style={{ fontSize: '15px', color: '#64748b', marginRight: '2px' }}>{prefix ? '#' + prefix + '-' : '#'}</span>
                                        <Input autoFocus size="sm" style={{ border: '2px solid #3b82f6', borderRadius: '6px', maxWidth: '180px' }} value={editingValue} onChange={(e) => setEditingValue(e.target.value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase())} onKeyDown={(e) => {
                                            if (e.key === 'Enter') { updateHashtagName(h.id, editingValue); }
                                            if (e.key === 'Escape') { setHashtags((prev) => prev.map((hs) => hs.id === h.id ? { ...hs, isEditing: false } : hs)); }
                                        }} />
                                        <BiCheck className="text-success ml-2" size={22} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); updateHashtagName(h.id, editingValue); }} />
                                        <BiX className="text-muted ml-1" size={22} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setHashtags((prev) => prev.map((hs) => hs.id === h.id ? { ...hs, isEditing: false } : hs)); }} />
                                    </div>
                                ) : (
                                    <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                                        <div>
                                            <div className="hashtag-name" style={h.recipients.length > 0 || h.existingRecipientCount > 0 ? {} : { color: '#9ca3af' }}>{prefix ? '#' + prefix + '-' + h.name.toLowerCase() : '#' + h.name}</div>
                                        </div>
                                        {!!h._id && (
                                            <BiEditAlt size={17} style={{ cursor: 'pointer', color: '#94a3b8', marginLeft: '4px', marginRight: '6px' }} onClick={(e) => { e.stopPropagation(); setEditingValue(h.name); setHashtags((prev) => prev.map((hs) => hs.id === h.id ? { ...hs, isEditing: true } : hs)); }} />
                                        )}
                                        {(h.recipients.length > 0 || h.existingRecipientCount > 0) && (
                                            <div className="recipient-badge" style={{ marginLeft: '2px', marginRight: '0' }}>
                                                <BiUser size={13} /> {Math.max(h.recipients.length, h.existingRecipientCount || 0)} recipients
                                            </div>
                                        )}
                                        {!!h._id && (
                                            <span className="action-link" style={{ color: '#3b82f6', marginLeft: '4px' }} onClick={(e) => { e.stopPropagation(); var orgId = user && user.profile && user.profile.organizationId && user.profile.organizationId._id; var hashtagName = prefix ? prefix + '-' + h.name.toLowerCase() : h.name.toLowerCase(); window.open('/home/trivia/' + orgId + '/user-chats/' + hashtagName + '/' + h._id, '_blank'); }}>View Chats</span>
                                        )}
                                    </div>
                                )}
                            </div>
                            {!h.isEditing && (
                                <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                    <div className="action-link" style={h.recipients.length > 0 || h.existingRecipientCount > 0 ? {} : { color: '#9ca3af' }}>{h._id ? 'Edit' : (deletedDefaultNames.has((h.standardName || h.name).toLowerCase()) ? 'Deleted' : 'Add Recipients')} <BiChevronRight size={16} /></div>
                                </div>
                            )}
                        </div>
                    ))}
                    <div className="sms-section-title">Custom Hashtags</div>
                    {hashtags.filter((h) => !h.isDefault).map((h) => (
                        <div key={h.id} className="hashtag-card" onClick={() => { if (!h.isEditing) goToPicker(h.id); }}>
                            <div className="d-flex align-items-center flex-grow-1">
                                <div className={`status-dot ${h.recipients.length > 0 || h.existingRecipientCount > 0 ? 'status-active' : 'status-inactive'}`} />
                                {h.isEditing ? (
                                    <div className="d-flex align-items-center" onClick={(e) => e.stopPropagation()}>
                                        <span className="mr-1" style={{ fontSize: '15px', color: '#64748b' }}>{prefix ? '#' + prefix + '-' : '#'}</span>
                                        <Input autoFocus size="sm" style={{ border: '2px solid #3b82f6', borderRadius: '6px', maxWidth: '180px' }} value={editingValue} onChange={(e) => setEditingValue(e.target.value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase())} onKeyDown={(e) => {
                                            if (e.key === 'Enter') { updateHashtagName(h.id, editingValue); }
                                            if (e.key === 'Escape') { if (!h.name) deleteHashtag(h.id); else setHashtags((prev) => prev.map((hs) => hs.id === h.id ? { ...hs, isEditing: false } : hs)); }
                                        }} />
                                        <BiCheck className="text-success ml-2" size={22} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); updateHashtagName(h.id, editingValue); }} />
                                        <BiX className="text-muted ml-1" size={22} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); if (!h.name) deleteHashtag(h.id); else setHashtags((prev) => prev.map((hs) => hs.id === h.id ? { ...hs, isEditing: false } : hs)); }} />
                                    </div>
                                ) : (
                                    <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                                        <div>
                                            <div className="hashtag-name" style={h.recipients.length > 0 || h.existingRecipientCount > 0 ? {} : { color: '#9ca3af' }}>{prefix ? '#' + prefix + '-' + h.name.toLowerCase() : '#' + h.name}</div>
                                        </div>
                                        {!!h._id && (
                                            <BiEditAlt size={17} style={{ cursor: 'pointer', color: '#94a3b8', marginLeft: '4px', marginRight: '6px' }} onClick={(e) => { e.stopPropagation(); setEditingValue(h.name); setHashtags((prev) => prev.map((hs) => hs.id === h.id ? { ...hs, isEditing: true } : hs)); }} />
                                        )}
                                        {(h.recipients.length > 0 || h.existingRecipientCount > 0) && (
                                            <div className="recipient-badge" style={{ marginLeft: '2px', marginRight: '0' }}>
                                                <BiUser size={13} /> {Math.max(h.recipients.length, h.existingRecipientCount || 0)} recipients
                                            </div>
                                        )}
                                        {!!h._id && (
                                            <span className="action-link" style={{ color: '#3b82f6', marginLeft: '4px' }} onClick={(e) => { e.stopPropagation(); var orgId = user && user.profile && user.profile.organizationId && user.profile.organizationId._id; var hashtagName = prefix ? prefix + '-' + h.name.toLowerCase() : h.name.toLowerCase(); window.open('/home/trivia/' + orgId + '/user-chats/' + hashtagName + '/' + h._id, '_blank'); }}>View Chats</span>
                                        )}
                                    </div>
                                )}
                            </div>
                            {!h.isEditing && (
                                <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                    <div className="action-link" style={h.recipients.length > 0 || h.existingRecipientCount > 0 ? {} : { color: '#9ca3af' }}>{h._id ? 'Edit' : 'Add Recipients'} <BiChevronRight size={16} /></div>
                                    <FiTrash2 className="hashtag-delete-icon" size={17} onClick={(e) => { e.stopPropagation(); setHashtagPendingDelete(h); }} />
                                </div>
                            )}
                        </div>
                    ))}
                    <button className="add-custom-btn" onClick={addCustomHashtag}>
                        <BiPlusCircle size={16} /> Add Custom Hashtag
                    </button>
                </div>
            )}
        </BottomScrollListener>
        {(() => {
            const orgId = user && user.profile && user.profile.organizationId && user.profile.organizationId._id;
            const hasPhone = newUserPhone && (newUserPhone || '').trim().length > 0;
            const hasAnyRecipients = hashtags.some(function(h) { return h.recipients.length > 0 || h.existingRecipientCount > 0; });
            const qrValue = hasPhone
                ? `${baseUrl().replace(/\/$/, '')}/start-chat?orgId=${orgId}&phone=${encodeURIComponent((newUserPhone || '').trim())}`
                : `${baseUrl().replace(/\/$/, '')}/start-chat?orgId=${orgId}`;
            const handleDownloadQR = () => {
                if (!hasAnyRecipients) return;
                const canvas = document.querySelector('#sms-qrcode-canvas');
                if (!canvas) { toastContainer('QR not ready.', 'error'); return; }
                const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
                const link = document.createElement('a');
                link.href = pngUrl;
                link.download = 'SMS Chat QR.png';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toastContainer('QR code downloaded.', 'success');
            };
            return (
                <div className="sms-routing-footer">
                    {/* TBV-844: the Auto-register toggle is frozen with the
                        phone/QR card rather than left in the scrolling list,
                        per the ticket ("freeze the bottom section that has the
                        Auto-Register toggle AND the Phone number and QR code"). */}
                    <div className="d-flex align-items-center justify-content-between mt-3 mb-2 px-2" style={{ padding: '20px 24px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: '15px', color: '#0f172a' }}>Auto-register participants</div>
                            <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px', lineHeight: 1.5 }}>Automatically register users who text a hashtag as platform participants</div>
                        </div>
                        <div
                            onClick={isSavingAutoRegister ? undefined : handleToggleAutoRegister}
                            style={{
                                width: '44px', height: '24px', borderRadius: '12px', cursor: isSavingAutoRegister ? 'not-allowed' : 'pointer',
                                backgroundColor: autoRegister ? '#0f172a' : '#cbd5e1', position: 'relative', transition: 'background-color 0.2s', flexShrink: 0, marginLeft: '16px'
                            }}
                        >
                            <div style={{
                                width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#fff', position: 'absolute', top: '2px',
                                left: autoRegister ? '22px' : '2px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                            }} />
                        </div>
                    </div>
                    <div className="sms-info-card mt-3" style={hasAnyRecipients ? {} : { opacity: 0.45, pointerEvents: 'none' }}>
                        <div className="info-card-header" style={hasAnyRecipients ? {} : { color: '#94a3b8' }}><BiPhone size={18} /> {hasAnyRecipients ? 'Active SMS Chats' : 'Inactive SMS Chats'}</div>
                        <div className="info-card-text">{hasAnyRecipients ? 'Share this phone number or download the QR code for user-initiated chats:' : 'Add at least one recipient to a hashtag to activate SMS chats.'}</div>
                        <div className="sms-action-row">
                            <div style={{ maxWidth: '200px' }}>
                                <Input
                                    type="text"
                                    value={newUserPhone}
                                    readOnly
                                    className="static-phone-input"
                                />
                            </div>
                            <button className="qr-download-btn" onClick={handleDownloadQR} disabled={!hasAnyRecipients}>
                                <MdQrCode size={16} /> Download QR
                            </button>
                        </div>
                    </div>
                    <div style={{ position: 'absolute', left: '-9999px' }}>
                        <QRCodeCanvas id="sms-qrcode-canvas" level="H" size={512} value={qrValue} />
                    </div>
                </div>
            );
        })()}
        </div>
    );

    const renderPickerStep = () => {
        if (!activeHashtag) return null;

        const savedRecipients = activeHashtag.recipients.filter(r => !r.isNew);
        const stagedRecipients = activeHashtag.recipients.filter(r => r.isNew);

        const activeMembers = savedRecipients.filter(r => !r.isMarkedForRemoval);
        const removingMembers = savedRecipients.filter(r => r.isMarkedForRemoval);

        const finalCount = activeMembers.length + stagedRecipients.length;

        const colors = ['#f87171', '#60a5fa', '#34d399', '#fbbf24', '#a78bfa', '#f472b6'];
        const getBg = (id) => colors[(id || '').charCodeAt(0) % colors.length] || '#94a3b8';

        const filterByName = (list) => {
            if (!currentTabSearch) return list;
            const s = currentTabSearch.toLowerCase();
            return list.filter(u =>
                (u.firstName || '').toLowerCase().includes(s) ||
                (u.lastName || '').toLowerCase().includes(s) ||
                (u.jobTitle || '').toLowerCase().includes(s) ||
                (u.department || '').toLowerCase().includes(s)
            );
        };

        const filteredStaged = filterByName(stagedRecipients);
        const filteredActive = filterByName(activeMembers);
        const filteredRemoving = filterByName(removingMembers);

        return (
            <div className="recipient-picker-step">
                <div className="d-flex justify-content-between align-items-center" style={{ paddingTop: '16px', marginBottom: '4px' }}>
                    <h6 className="mb-0" style={{ fontWeight: '700', fontSize: '15px' }}>
                        Recipients for <span style={{ color: '#3b82f6' }}>{prefix ? '#' + prefix + '-' + activeHashtag.name.toLowerCase() : '#' + activeHashtag.name}</span>
                    </h6>
                    <div style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'right' }}>
                        {finalCount} selected
                    </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                    <div className="d-flex justify-content-between align-items-end">
                        <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
                            Description (optional)
                        </div>
                    </div>
                    <Input
                        type="textarea"
                        placeholder="Briefly describe the purpose of this hashtag group..."
                        value={activeHashtag.description || ''}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val.length <= 500) {
                                setHashtags((prev) => prev.map(h => h.id === activeHashtag.id ? { ...h, description: val } : h));
                            }
                        }}
                        style={{ height: '70px', resize: 'none', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#fff' }}
                    />
                </div>

                <div style={{ marginBottom: '24px' }}>
                    <div className="d-flex align-items-center" style={{ marginBottom: '8px' }}>
                        <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            Alert Notifications
                        </div>
                        <OoPopover
                            target={`notification_frequency_${activeHashtag.id}`}
                            placement="right"
                            headerText="Alert Notifications"
                            bodyText="Frequency of email and text notifications to all recipients when a user sends a message."
                        />
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: '24px', marginBottom: '10px' }}>
                        <div style={{ width: '52px', fontSize: '14px', color: '#1e293b' }}>Email</div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 0, cursor: 'pointer', fontSize: '14px', color: '#1e293b' }}>
                            <input
                                type="radio"
                                name={`notificationFrequency_${activeHashtag.id}`}
                                checked={!emailAlertIsNone && (activeHashtag.notificationFrequency || NOTIFICATION_FREQUENCY.FIRST_TIME) === NOTIFICATION_FREQUENCY.FIRST_TIME}
                                onChange={() => {
                                    setEmailAlertIsNone(false);
                                    setHashtags((prev) => prev.map(h => h.id === activeHashtag.id ? { ...h, notificationFrequency: NOTIFICATION_FREQUENCY.FIRST_TIME } : h));
                                }}
                                style={{ cursor: 'pointer', accentColor: '#3b82f6' }}
                            />
                            First Time
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 0, cursor: 'pointer', fontSize: '14px', color: '#1e293b' }}>
                            <input
                                type="radio"
                                name={`notificationFrequency_${activeHashtag.id}`}
                                checked={!emailAlertIsNone && (activeHashtag.notificationFrequency || NOTIFICATION_FREQUENCY.FIRST_TIME) === NOTIFICATION_FREQUENCY.EVERY_TIME}
                                onChange={() => {
                                    setEmailAlertIsNone(false);
                                    setHashtags((prev) => prev.map(h => h.id === activeHashtag.id ? { ...h, notificationFrequency: NOTIFICATION_FREQUENCY.EVERY_TIME } : h));
                                }}
                                style={{ cursor: 'pointer', accentColor: '#3b82f6' }}
                            />
                            Every Time
                        </label>
                        {/* TBV-916 update: demo-only — never written to activeHashtag, so Save still sends the real First/Every Time value underneath. */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 0, cursor: 'pointer', fontSize: '14px', color: '#1e293b' }}>
                            <input
                                type="radio"
                                name={`notificationFrequency_${activeHashtag.id}`}
                                checked={emailAlertIsNone}
                                onChange={() => setEmailAlertIsNone(true)}
                                style={{ cursor: 'pointer', accentColor: '#3b82f6' }}
                            />
                            None
                        </label>
                    </div>
                    {/* TBV-916: demo-only — visually matches Email above but is local state, not sent to the backend. */}
                    <div className="d-flex align-items-center" style={{ gap: '24px' }}>
                        <div style={{ width: '52px', fontSize: '14px', color: '#1e293b' }}>SMS</div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 0, cursor: 'pointer', fontSize: '14px', color: '#1e293b' }}>
                            <input
                                type="radio"
                                name={`smsAlertFrequency_${activeHashtag.id}`}
                                checked={smsAlertFrequency === NOTIFICATION_FREQUENCY.FIRST_TIME}
                                onChange={() => setSmsAlertFrequency(NOTIFICATION_FREQUENCY.FIRST_TIME)}
                                style={{ cursor: 'pointer', accentColor: '#3b82f6' }}
                            />
                            First Time
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 0, cursor: 'pointer', fontSize: '14px', color: '#1e293b' }}>
                            <input
                                type="radio"
                                name={`smsAlertFrequency_${activeHashtag.id}`}
                                checked={smsAlertFrequency === NOTIFICATION_FREQUENCY.EVERY_TIME}
                                onChange={() => setSmsAlertFrequency(NOTIFICATION_FREQUENCY.EVERY_TIME)}
                                style={{ cursor: 'pointer', accentColor: '#3b82f6' }}
                            />
                            Every Time
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 0, cursor: 'pointer', fontSize: '14px', color: '#1e293b' }}>
                            <input
                                type="radio"
                                name={`smsAlertFrequency_${activeHashtag.id}`}
                                checked={smsAlertFrequency === NOTIFICATION_FREQUENCY.NONE}
                                onChange={() => setSmsAlertFrequency(NOTIFICATION_FREQUENCY.NONE)}
                                style={{ cursor: 'pointer', accentColor: '#3b82f6' }}
                            />
                            None
                        </label>
                    </div>
                </div>

                <div className="status-pills-row">
                    {savedRecipients.filter(r => r.isMarkedForRemoval).length > 0 && (
                        <div className="status-pill status-pill--remove">-{savedRecipients.filter(r => r.isMarkedForRemoval).length} to remove</div>
                    )}
                </div>

                <div className="picker-tabs-container">
                    <button className={`picker-tab ${pickerTab === 'current' ? 'picker-tab--active' : ''}`} onClick={() => setPickerTab('current')}>
                        <BiUser size={18} /> Current ({finalCount})
                    </button>
                    <button className={`picker-tab ${pickerTab === 'find' ? 'picker-tab--active' : ''}`} onClick={() => setPickerTab('find')}>
                        <BiUserPlus size={18} /> Find Recipients
                    </button>
                </div>

                <div style={{ display: pickerTab === 'current' ? 'block' : 'none' }}>
                    <div className="user-list-wrapper" style={{ border: 'none', background: 'transparent' }}>
                        {/* {stagedRecipients.length > 0 && (
                            <div className="search-current-wrapper">
                                <BiSearch className="search-current-icon" />
                                <input
                                    type="text"
                                    className="search-current-users-input"
                                    placeholder="Search current users..."
                                    value={currentTabSearch}
                                    onChange={(e) => setCurrentTabSearch(e.target.value)}
                                />
                            </div>
                        )} */}

                        <div style={{ maxHeight: '420px', overflowY: 'auto', paddingRight: '4px' }}>
                            {stagedRecipients.length > 0 && (
                                <>
                                    <div className="current-section-header current-section-header--pending">Pending Addition</div>
                                    {filteredStaged.map((u) => (
                                        <div key={u.userId} className="user-list-item user-list-item--pending">
                                            <div className="initials-avatar" style={{ backgroundColor: getBg(u.userId) }}>{getInitials(u)}</div>
                                            <div className="user-info-main">
                                                <div className="user-full-name">{u.firstName} {u.lastName}</div>
                                                <div className="user-meta-sub">{[u.jobTitle, u.department].filter(Boolean).join(' • ')}</div>
                                            </div>
                                            <BiX size={20} className="remove-staged-btn" onClick={() => toggleRecipient(u)} />
                                        </div>
                                    ))}
                                    <div style={{ marginBottom: '24px' }} />
                                </>
                            )}

                            {(activeMembers.length > 0 || removingMembers.length > 0) && (
                                <>
                                    {stagedRecipients.length > 0 && <div className="current-section-header current-section-header--active">Active Members</div>}
                                    {filteredActive.map((u) => (
                                        <div key={u.userId} className="user-list-item user-list-item--active-member">
                                            <div className="initials-avatar" style={{ backgroundColor: getBg(u.userId) }}>{getInitials(u)}</div>
                                            <div className="user-info-main">
                                                <div className="user-full-name">{u.firstName} {u.lastName}</div>
                                                <div className="user-meta-sub">{[u.jobTitle, u.department].filter(Boolean).join(' • ')}</div>
                                            </div>
                                            <FiTrash2
                                                size={18}
                                                className="active-member-remove-btn"
                                                onClick={() => removeRecipient(u.userId)}
                                            />
                                        </div>
                                    ))}
                                    {filteredRemoving.map((u) => (
                                        <div key={u.userId} className="user-list-item user-list-item--removed">
                                            <div className="initials-avatar" style={{ backgroundColor: getBg(u.userId) }}>{getInitials(u)}</div>
                                            <div className="user-info-main">
                                                <div className="user-full-name">{u.firstName} {u.lastName}</div>
                                                <div className="user-meta-sub">{[u.jobTitle, u.department].filter(Boolean).join(' • ')}</div>
                                            </div>
                                            <button className="undo-btn" onClick={() => undoRemoveRecipient(u.userId)}>Undo</button>
                                        </div>
                                    ))}
                                </>
                            )}

                            {filteredStaged.length === 0 && filteredActive.length === 0 && filteredRemoving.length === 0 && (
                                <div className="text-center text-muted p-5 bg-light rounded" style={{ border: '1px dashed #cbd5e1' }}>
                                    {currentTabSearch ? 'No matching recipients found.' : 'No recipients assigned yet.'}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div style={{ display: pickerTab === 'find' ? 'block' : 'none' }}>
                        <div className="filter-selector-wrapper">
                            <FilterSelector key={findTabKey} allMetaData={allMetaData} handleApplyFilters={handleApplyFilters} />
                        </div>
                        <div className="user-list-wrapper">
                            {filteredUsers.length > 0 && (
                                <div style={{ color: '#64748b', fontSize: '14px', padding: '8px' }}>
                                    <div>Select recipient(s) for this Hashtag by checking their box.</div>
                                    <div>Note: Emails are required for recipients.</div>
                                </div>
                            )}
                            <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
                                {stagedRecipients.length > 0 && (
                                    <>
                                        {stagedRecipients.map((u) => (
                                            <div key={u.userId} className="user-list-item user-list-item--staged">
                                                <div className="d-flex align-items-center" onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        checked={true}
                                                        onChange={() => toggleRecipient(u)}
                                                        style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#3b82f6' }}
                                                    />
                                                </div>
                                                <div className="initials-avatar" style={{ backgroundColor: getBg(u.userId) }}>{getInitials(u)}</div>
                                                <div className="user-info-main">
                                                    <div className="user-full-name">{u.firstName} {u.lastName}</div>
                                                    <div className="user-meta-sub">{[u.jobTitle, u.department, u.email].filter(Boolean).join(' • ')}</div>
                                                </div>
                                                <BiX size={18} className="text-muted" style={{ cursor: 'pointer' }} onClick={() => toggleRecipient(u)} />
                                            </div>
                                        ))}
                                    </>
                                )}

                                {usersLoading ? (
                                    <div className="text-center p-5 text-muted"><i className="fa fa-spinner fa-spin mr-2"></i> Loading users...</div>
                                ) : filteredUsers.filter(u => !activeHashtag.recipients.some(r => r.userId === u.userId)).length > 0 ? (
                                    filteredUsers.filter(u => !activeHashtag.recipients.some(r => r.userId === u.userId)).map((u) => (
                                        <div key={u.userId} className="user-list-item" onClick={() => toggleRecipient(u)} style={{ cursor: 'pointer' }}>
                                            <div className="d-flex align-items-center" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={false}
                                                    onChange={() => toggleRecipient(u)}
                                                    style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#3b82f6' }}
                                                />
                                            </div>
                                            <div className="initials-avatar" style={{ backgroundColor: getBg(u.userId), opacity: 0.8 }}>{getInitials(u)}</div>
                                            <div className="user-info-main">
                                                <div className="user-full-name">{u.firstName} {u.lastName}</div>
                                                <div className="user-meta-sub">{[u.jobTitle, u.department, u.email].filter(Boolean).join(' • ')}</div>
                                            </div>
                                        </div>
                                    ))
                                ) : filteredUsers.length > 0 && filteredUsers.every(function(u) { return (activeHashtag.initialRecipients || []).indexOf(u.userId) > -1; }) ? (
                                    <div className="text-center text-muted p-4">All matching users are already recipients.</div>
                                ) : filteredUsers.length === 0 ? (
                                    <div className="text-center text-muted p-5 bg-light" style={{ margin: '10px', border: '1px dashed #cbd5e1', borderRadius: '12px' }}>
                                        <div>No users found. Use the filters above to search for recipients.</div>
                                        <div className="mt-2">Note: Emails are required for recipients.</div>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                </div>
                <div className="d-flex justify-content-end mt-4 pt-3 border-top" style={{ gap: '10px' }}>
                    <Button color="secondary" outline onClick={goToList} style={{ borderRadius: '8px', padding: '9px 24px', fontWeight: '600', border: 'none', color: '#1e293b' }}>Cancel</Button>
                    <Button color="primary" onClick={handleSaveRecipients} style={{ borderRadius: '12px', padding: '10px 28px', fontWeight: '700', backgroundColor: '#3b82f6', border: 'none' }}>Save</Button>
                </div>
            </div>
        );
    };

    return (
        <>
            <Modal isOpen={isOpen} toggle={toggle} size="lg" centered className="sms-routing-modal">
                <ModalHeader toggle={toggle}>
                    <BiMessageSquareDetail className="text-primary" /> Set Up User-Initiated SMS Chats
                    {currentStep === 1 && (
                        <button
                            type="button"
                            className={'recover-deleted-btn' + (deletedHashtags.length > 0 ? ' has-deleted' : '')}
                            onClick={() => setIsRecoverModalOpen(true)}
                        >
                            <FiArchive size={13} /> Recover deleted{deletedHashtags.length > 0 ? ` (${deletedHashtags.length})` : ''}
                        </button>
                    )}
                </ModalHeader>
                <ModalBody>
                    <div style={{ color: '#64748b', fontSize: '14px', marginBottom: '4px' }}>Set up hashtags and assign recipients for routing of user-initiated SMS chats.</div>
                    {currentStep === 0 ? renderPrefixStep() : currentStep === 1 ? renderListStep() : renderPickerStep()}
                </ModalBody>
            </Modal>
            <DeleteHashtagModal
                isOpen={!!hashtagPendingDelete}
                hashtag={hashtagPendingDelete}
                prefix={prefix}
                onClose={() => setHashtagPendingDelete(null)}
                onDeleted={handleHashtagDeleted}
            />
            <RecoverDeletedHashtagsModal
                isOpen={isRecoverModalOpen}
                onClose={() => setIsRecoverModalOpen(false)}
                deletedHashtags={deletedHashtags}
                prefix={prefix}
                loading={isLoadingDeleted}
                hasMore={hasMoreDeleted}
                onLoadMore={() => fetchDeletedHashtags(deletedNextSkip)}
                onRestored={handleHashtagRestored}
            />
        </>
    );
};

const mapDispatchToProps = (dispatch) => ({
    onUpdateOrgPrefix: (val) => dispatch(orgSetupSkipped(val)),
});

export default connect(null, mapDispatchToProps)(SmsRoutingModal);
